/** CollectionGrid — AJAX filtering, sorting, and load-more for collection/search pages.
 *  Depends on parseSectionHtml() from main.js. */
class CollectionGrid extends HTMLElement {
  connectedCallback() {
    this.isCollection = this.classList.contains("main-collection");
    this.context = this.isCollection ? "collection" : "search";
    this.baseUrl = this.isCollection ? this.dataset.collectionUrl : this.dataset.searchUrl;
    this.sectionId = this.dataset.sectionId;

    this.productGrid = this.querySelector("#ProductGrid");
    this.filterForm = this.querySelector("#FacetFiltersForm");
    this.loadMoreBtn = this.querySelector("#LoadMoreBtn");
    this.filterToggle = this.querySelector("[data-filters-toggle]");
    this.layoutBody = this.querySelector(".collection-layout__body");
    this.productCount = this.querySelector("#ProductCount");
    this.mobileDialog = this.querySelector("#FiltersDrawer");
    this.mobileFilterForm = this.querySelector("#FacetFiltersFormMobile");
    this.mobileFiltersToggle = this.querySelector("[data-mobile-filters-toggle]");

    this.isLoading = false;
    this.hasNextPage = true;
    this.currentPage = 1;
    this.fetchController = null;
    this.sectionParam = `section_id=${this.sectionId}`;

    this.liveRegion = document.createElement("div");
    this.liveRegion.setAttribute("aria-live", "polite");
    this.liveRegion.setAttribute("role", "status");
    this.liveRegion.className = "visually-hidden";
    document.body.appendChild(this.liveRegion);

    this.restoreScrollPosition();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.liveRegion?.remove();
    this.fetchController?.abort();
  }

  // ─── Scroll restore ──────────────────────────────────────────────────────
  restoreScrollPosition() {
    const navEntry = performance.getEntriesByType("navigation")[0];
    const raw = navEntry?.type === "back_forward" ? sessionStorage.getItem(`${this.context}-state`) : null;
    sessionStorage.removeItem(`${this.context}-state`);

    if (!raw) return;

    let state;
    try {
      state = JSON.parse(raw);
    } catch {
      return;
    }

    if (state.url !== this.baseUrl) return;

    this.productGrid.innerHTML = state.html;
    window.scrollTo(0, Math.min(state.scrollPosition, document.documentElement.scrollHeight));

    this.currentPage = state.page;
    this.hasNextPage = state.hasNextPage;
    if (!this.hasNextPage) {
      (this.loadMoreBtn?.closest(".load-more-wrapper") ?? this.loadMoreBtn)?.remove();
      this.loadMoreBtn = null;
    }
  }

  // ─── Event binding ───────────────────────────────────────────────────────
  bindEvents() {
    if (this.filterToggle && this.querySelector("#CollectionFilters")) {
      const isOpen = this.layoutBody.classList.contains("filters-open");
      this.filterToggle.setAttribute("aria-expanded", String(isOpen));
      this.filterToggle.addEventListener("click", () => this.toggleFilters());
    }

    this.filterForm?.addEventListener("change", () => this.applyFilters(this.filterForm));
    this.mobileFilterForm?.addEventListener("change", () => this.applyFilters(this.mobileFilterForm));
    this.addEventListener("collection:sort", () => this.applyFilters());

    this.mobileFiltersToggle?.addEventListener("click", () => this.mobileDialog?.showModal());

    this.addEventListener("click", e => {
      const link = e.target.closest("#ActiveFilters a");
      if (!link) return;
      e.preventDefault();
      this.applyFromUrl(link.href);
    });

    this.bindLoadMore(this.loadMoreBtn);

    this.productGrid?.addEventListener("click", e => {
      if (!e.target.closest("a")?.href) return;
      if (this.productGrid.querySelector(".collection-empty")) return;
      try {
        sessionStorage.setItem(`${this.context}-state`, JSON.stringify({
          url: this.baseUrl,
          html: this.productGrid.innerHTML,
          scrollPosition: window.scrollY,
          page: this.currentPage,
          hasNextPage: this.hasNextPage,
        }));
      } catch {
        // Silently fail in private browsing or when storage is full
      }
    });
  }

  bindLoadMore(btn) {
    btn?.addEventListener("click", e => {
      e.preventDefault();
      this.loadNextPage();
    });
  }

  // ─── URL helpers ─────────────────────────────────────────────────────────
  buildUrl(base, params = "") {
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}${this.sectionParam}${params ? `&${params}` : ""}`;
  }

  getSearchParams(sourceForm = this.filterForm) {
    const params = new URLSearchParams();
    if (sourceForm) {
      new FormData(sourceForm).forEach((v, k) => {
        if (v === "") return;
        params.append(k, v);
      });
    }
    const sortSelect = this.querySelector('select[name="sort_by"]');
    if (sortSelect?.value) params.set("sort_by", sortSelect.value);
    return params;
  }

  replaceFormContent(form, newForm) {
    const openLabels = new Set([...form.querySelectorAll("details[open] summary")].map(s => s.textContent.trim()));
    form.innerHTML = newForm.innerHTML;
    form.querySelectorAll("details").forEach(d => {
      if (openLabels.has(d.querySelector("summary")?.textContent.trim())) d.open = true;
    });
  }

  // ─── Loader ──────────────────────────────────────────────────────────────
  showLoader(message = this.dataset.loadingMsg || "Loading…") {
    this.dataset.loading = "";
    this.liveRegion.textContent = message;
  }

  hideLoader() {
    delete this.dataset.loading;
  }

  // ─── Load more button ────────────────────────────────────────────────────
  updateLoadMoreBtn(section) {
    const newBtn = section?.querySelector("#LoadMoreBtn");
    const existing = this.querySelector("#LoadMoreBtn");
    (existing?.closest(".load-more-wrapper") ?? existing)?.remove();

    if (newBtn) {
      const toInsert = newBtn.closest(".load-more-wrapper") ?? newBtn;
      this.productGrid.after(toInsert);
      this.loadMoreBtn = newBtn;
      this.bindLoadMore(this.loadMoreBtn);
    } else {
      this.hasNextPage = false;
      this.loadMoreBtn = null;
    }
  }

  // ─── Filter / sort ───────────────────────────────────────────────────────
  applyFilters(sourceForm = this.filterForm) {
    const params = this.getSearchParams(sourceForm);
    const fetchUrl = this.buildUrl(this.baseUrl, params);
    const sep = this.baseUrl.includes("?") ? "&" : "?";
    this.fetchSection(fetchUrl, `${this.baseUrl}${sep}${params}`);
  }

  applyFromUrl(url) {
    this.fetchSection(this.buildUrl(url), url);
  }

  fetchSection(fetchUrl, finalUrl) {
    this.fetchController?.abort();
    this.fetchController = new AbortController();

    this.showLoader();

    fetch(fetchUrl, { signal: this.fetchController.signal })
      .then(r => r.text())
      .then(html => {
        const section = Utility.parseSectionHtml(html);

        const newGrid = section?.querySelector("#ProductGrid");
        if (this.productGrid && newGrid) this.productGrid.innerHTML = newGrid.innerHTML;

        const newDesktopForm = section?.querySelector("#FacetFiltersForm");
        if (this.filterForm && newDesktopForm) this.replaceFormContent(this.filterForm, newDesktopForm);

        const newMobileForm = section?.querySelector("#FacetFiltersFormMobile");
        if (this.mobileFilterForm && newMobileForm) this.replaceFormContent(this.mobileFilterForm, newMobileForm);

        const newActiveFilters = section?.querySelector("#ActiveFilters");
        const currentActiveFilters = this.querySelector("#ActiveFilters");
        if (currentActiveFilters && newActiveFilters) currentActiveFilters.replaceWith(newActiveFilters);

        const newCount = section?.querySelector("#ProductCount");
        if (this.productCount && newCount) this.productCount.textContent = newCount.textContent;

        this.currentPage = 1;
        this.hasNextPage = true;

        this.updateLoadMoreBtn(section);
        this.hideLoader();

        window.history.replaceState({}, "", finalUrl);

        const isMobile = window.innerWidth < 768;
        const scrollTarget = isMobile
          ? (this.querySelector('#ActiveFilters') || this.productGrid)
          : (this.closest('.main-collection') || this);
        if (scrollTarget) {
          const stickyOffset = [...document.querySelectorAll('header, [data-sticky-header], .utility-bar')]
            .reduce((sum, el) => sum + el.getBoundingClientRect().height, 0);
          const top = scrollTarget.getBoundingClientRect().top + window.scrollY - stickyOffset;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
      })
      .catch(err => {
        if (err.name === "AbortError") return;
        console.error("CollectionGrid: fetch error", err);
        this.hideLoader();
        this.liveRegion.textContent = this.dataset.errorMsg || "Error loading products. Please try again.";
      });
  }

  toggleFilters() {
    const isOpen = this.layoutBody.classList.contains("filters-open");
    this.layoutBody.classList.toggle("filters-open", !isOpen);
    this.filterToggle.setAttribute("aria-expanded", String(!isOpen));
  }

  loadNextPage() {
    if (this.isLoading || !this.hasNextPage) return;

    this.isLoading = true;
    this.currentPage++;

    if (this.loadMoreBtn) {
      const spinner = document.createElement("span");
      spinner.className = "spinner";
      spinner.setAttribute("aria-hidden", "true");
      this.loadMoreBtn.appendChild(spinner);
      this.loadMoreBtn.classList.add("btn--loading");
      this.loadMoreBtn.dataset.loading = "";
      this.loadMoreBtn.disabled = true;
    }

    const params = this.getSearchParams();
    params.set("page", this.currentPage);

    const url = this.buildUrl(this.baseUrl, params);

    fetch(url)
      .then(r => r.text())
      .then(html => {
        const section = Utility.parseSectionHtml(html);
        const newItems = section?.querySelectorAll("#ProductGrid > *") ?? [];

        if (!newItems.length) {
          this.hasNextPage = false;
          (this.loadMoreBtn?.closest(".load-more-wrapper") ?? this.loadMoreBtn)?.remove();
          this.isLoading = false;
          return;
        }

        newItems.forEach(item => this.productGrid.appendChild(item));

        this.updateLoadMoreBtn(section);
        this.isLoading = false;
      })
      .catch(err => {
        console.error("CollectionGrid: load more error", err);
        if (this.loadMoreBtn) {
          this.loadMoreBtn.querySelector(".spinner")?.remove();
          this.loadMoreBtn.classList.remove("btn--loading");
          delete this.loadMoreBtn.dataset.loading;
          this.loadMoreBtn.disabled = false;
        }
        this.isLoading = false;
      });
  }
}

if (!customElements.get("collection-grid")) customElements.define("collection-grid", CollectionGrid);
